import { Button, Input, Space, Table, Tag, Progress } from "antd";
import { EditOutlined } from "@ant-design/icons";
import dayjs from "dayjs";
import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { loadAllStaff } from "../../redux/rtk/features/user/userSlice";
import ColVisibilityDropdown from "../Shared/ColVisibilityDropdown";
import ViewBtn from "../Buttons/ViewBtn";
import BtnViewSvg from "../UI/Button/btnViewSvg";

function GetAllPIPs({ data = [], onEdit, onRefresh }) {
	const dispatch = useDispatch();
	const [columnsToShow, setColumnsToShow] = useState([]);
	const [searchEmployee, setSearchEmployee] = useState("");
	const [searchManager, setSearchManager] = useState("");
	const [filterStatus, setFilterStatus] = useState("");
	const staffList = useSelector((state) => state.users?.list || []);

	useEffect(() => {
		dispatch(loadAllStaff({ status: true }));
	}, [dispatch]);

	const mapEmployeeName = (employeeId) => {
		const employee = staffList.find((e) => e.id === employeeId);
		if (employee) {
			return (
				employee.EmployeeName ||
				`${employee.firstName || ""} ${employee.lastName || ""}`.trim() ||
				employee.UserName
			);
		}
		return employeeId;
	};

	const calculateProgress = (pip) => {
		if (!pip.startDate || !pip.endDate) return 0;
		const start = dayjs(pip.startDate);
		const end = dayjs(pip.endDate);
		const now = dayjs();
		const totalDays = end.diff(start, "day");
		const elapsedDays = now.diff(start, "day");
		if (totalDays <= 0) return 100;
		const progress = Math.min(100, Math.max(0, (elapsedDays / totalDays) * 100));
		return Math.round(progress);
	};

	const getStatusColor = (status) => {
		switch (status) {
			case "Completed":
				return "green";
			case "Active":
				return "blue";
			case "Under Review":
				return "orange";
			case "Terminated":
				return "red";
			default:
				return "default";
		}
	};

	const getDaysRemaining = (endDate) => {
		if (!endDate) return "-";
		const end = dayjs(endDate);
		const now = dayjs();
		const days = end.diff(now, "day");
		if (days < 0) return "Overdue";
		return `${days} days`;
	};

	const columns = [
		{
			id: 1,
			title: "S.No",
			dataIndex: "serial",
			key: "serial",
		},
		{
			title: "Employee",
			dataIndex: "employeeName",
			key: "employeeName",
		},
		{
			title: "Manager",
			dataIndex: "managerName",
			key: "managerName",
		},
		{
			title: "Start Date",
			dataIndex: "startDate",
			key: "startDate",
			render: (date) => (date ? dayjs(date).format("DD-MM-YYYY") : "-"),
		},
		{
			title: "End Date",
			dataIndex: "endDate",
			key: "endDate",
			render: (date) => (date ? dayjs(date).format("DD-MM-YYYY") : "-"),
		},
		{
			title: "Days Remaining",
			key: "daysRemaining",
			render: (_, record) => {
				const days = getDaysRemaining(record.endDate);
				const color = days === "Overdue" ? "red" : parseInt(days) < 7 ? "orange" : "default";
				return <Tag color={color}>{days}</Tag>;
			},
		},
		{
			title: "Progress",
			key: "progress",
			render: (_, record) => {
				const progress = calculateProgress(record);
				const color = progress >= 100 ? "green" : progress >= 75 ? "blue" : progress >= 50 ? "orange" : "red";
				return (
					<Progress
						percent={progress}
						size="small"
						status={progress >= 100 ? "success" : "active"}
						strokeColor={color === "green" ? "#52c41a" : color === "blue" ? "#1890ff" : color === "orange" ? "#faad14" : "#ff4d4f"}
					/>
				);
			},
		},
		{
			title: "Status",
			dataIndex: "status",
			key: "status",
			render: (status) => (
				<Tag color={getStatusColor(status)}>
					{status || "Draft"}
				</Tag>
			),
		},
		{
			title: "Actions",
			key: "action",
			render: (_, record) => {
				const id = record.id;
				return (
					<Space>
						<ViewBtn
							path={`/admin/pip/${id}`}
							text="View"
							icon={<BtnViewSvg />}
						/>
						<Button
							type="link"
							icon={<EditOutlined />}
							onClick={() => onEdit && onEdit(record)}
						/>
					</Space>
				);
			},
		},
	];

	useEffect(() => {
		setColumnsToShow(columns);
	}, []);

	const columnsToShowHandler = (val) => {
		setColumnsToShow(val);
	};

	const addKeys = (arr) =>
		(Array.isArray(arr) ? arr : []).map((i, index) => ({
			...i,
			key: i.id || index,
			serial: index + 1,
			employeeName: i.employeeName || mapEmployeeName(i.employeeId),
			managerName: i.managerName || mapEmployeeName(i.managerId),
		}));

	const filteredData = addKeys(data).filter((item) => {
		const employeeName = (item.employeeName || "").toLowerCase();
		const managerName = (item.managerName || "").toLowerCase();
		const matchesEmployee = employeeName.includes(searchEmployee.toLowerCase());
		const matchesManager = managerName.includes(searchManager.toLowerCase());
		const matchesStatus = !filterStatus || item.status === filterStatus;
		return matchesEmployee && matchesManager && matchesStatus;
	});

	return (
		<div className="pip-table-container">
			<div className="text-center my-2 flex justify-between items-center">
				<h5 className="text-color-2 text-xl mb-2">Performance Improvement Plans List</h5>
				{data && data.length > 0 && (
					<div style={{ display: "flex", alignItems: "center", gap: "10px", flexWrap: "wrap" }}>
						<ColVisibilityDropdown
							options={columns}
							columns={columns}
							columnsToShowHandler={columnsToShowHandler}
						/>
						<Input
							placeholder="Search by Employee"
							value={searchEmployee}
							onChange={(e) => setSearchEmployee(e.target.value)}
							style={{ width: 150 }}
						/>
						<Input
							placeholder="Search by Manager"
							value={searchManager}
							onChange={(e) => setSearchManager(e.target.value)}
							style={{ width: 150 }}
						/>
						<Input
							placeholder="Filter by Status"
							value={filterStatus}
							onChange={(e) => setFilterStatus(e.target.value)}
							style={{ width: 150 }}
						/>
					</div>
				)}
			</div>

			<Table
				scroll={{ x: true, y: "calc(100vh - 350px)" }}
				loading={false}
				columns={columnsToShow}
				dataSource={filteredData}
				pagination={{
					pageSize: 10,
					showSizeChanger: true,
					showTotal: (total, range) =>
						`${range[0]}-${range[1]} of ${total} items`,
					pageSizeOptions: ["10", "20", "50", "100"],
				}}
			/>
		</div>
	);
}

export default GetAllPIPs;

